import React from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { useCopies } from "../../hooks/useCopies";
import useSponsorAdBrandingService from "./service";
import { AdDataType, AdOrientation, SponsorAdArgsType, SponsorHeadingArgsType } from "./type";
import { LogAnalyticsFunctionType } from "../../../apps/myExperience/context/analyticsWrapper/types";
import InventorySubType from "../../enums/InventorySubType";
import { AnalyticsActionNameEnum, AnalyticsEventNameEnum } from "../../enums/AnalyticsEnums";
import { getAnalyticsScreenType } from "./util";
import Button from "../button";
import { getUrlForHref } from "shared/utils";
import { validUrlRegex } from "shared/utils/regex";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

function SponsorAdBranding({ showCloseButton = true, ...props }: SponsorAdArgsType) {
  const service = useSponsorAdBrandingService(props);

  if (!service.showSponsorAd) {
    return null;
  }
  return (
    <React.Fragment>
      <div
        className={classNames("sponsorAd_wrapper", props.className, {
          hasHeader: service.displayHeader,
          "sponsorAdBranding--vertical": props.orientation === AdOrientation.VERTICAL,
          "sponsorAdBranding--horizontal": props.orientation === AdOrientation.HORIZONTAL
        })}>
        {service.displayHeader && (
          <SponsorHeading
            title={props.title}
            onClose={service.sponsorAdCloseHandler}
            showCloseButton={showCloseButton}
          />
        )}
        <SponsorBody {...props} />
      </div>
    </React.Fragment>
  );
}

export default SponsorAdBranding;

function SponsorHeading({ title, onClose, showCloseButton }: SponsorHeadingArgsType) {
  const copies = useCopies();
  return (
    <div className="sponsorAdBranding_head">
      <span>{title || copies.get("sponsor.ad.title")}</span>
      <Button
        icon={<Icon.Close />}
        disabled={!showCloseButton}
        onClick={onClose}
        className="sponsorAdBranding_head_close"
      />
    </div>
  );
}

function SponsorBody({ logAnalyticsEvent, ...props }: SponsorAdArgsType) {
  const openLinkInNewTab = React.useCallback((adObj: AdDataType) => {
    adObj.sponsorRedirectUrl && window.open(getUrlForHref(adObj.sponsorRedirectUrl), "_blank");
  }, []);

  const logAnalyticsClickEvent = React.useCallback(
    (adObj: AdDataType) => {
      logAnalyticsEvent?.(
        AnalyticsEventNameEnum.DIGITAL_ADS,
        {
          action: AnalyticsActionNameEnum.CLICK,
          screenType: getAnalyticsScreenType(props.inventorySubType),
          contextEntityName: adObj.sponsorName,
          contextEntityId: adObj.sponsorUrl || adObj.sponsorRedirectUrl
        },
        AnalyticsDatastoreType.MIXPANEL_BUSINESS
      );
    },
    [logAnalyticsEvent, props.inventorySubType]
  );

  return (
    <div className="sponsorAdBranding_body">
      <div className="sponsorAdBranding_list">
        {props.adList.map((adObj, index) => {
          const isBannerClickable = Boolean(adObj.sponsorRedirectUrl && !adObj.sponsorUrl);
          return (
            <div className="sponsorAdBranding_list_item" key={index}>
              {adObj.digitalSpaceId && (
                <RegisterImpressionInGA
                  adObj={adObj}
                  inventorySubType={props.inventorySubType}
                  logAnalyticsEvent={logAnalyticsEvent}
                />
              )}
              <div key={index} className="sponsorAdBranding" onClick={() => logAnalyticsClickEvent(adObj)}>
                <img
                  className={classNames("sponsorAdBranding_img", {
                    "sponsorAdBranding_img--clickable": isBannerClickable
                  })}
                  src={adObj.sponsorImgUrl}
                  alt={adObj.sponsorName}
                  onClick={() => isBannerClickable && openLinkInNewTab(adObj)}
                />
                {adObj.sponsorContent && (
                  <p className="sponsorAdBranding_content" dangerouslySetInnerHTML={{ __html: adObj.sponsorContent }} />
                )}
                {adObj.sponsorUrl && (
                  <React.Fragment>
                    {validUrlRegex.test(adObj.sponsorUrl) || adObj.sponsorRedirectUrl ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        className="sponsorAdBranding_link"
                        href={
                          (adObj.sponsorRedirectUrl && getUrlForHref(adObj.sponsorRedirectUrl)) ||
                          getUrlForHref(adObj.sponsorUrl)
                        }>
                        {adObj.sponsorUrl}
                      </a>
                    ) : (
                      <span className="sponsorAdBranding_link">{adObj.sponsorUrl}</span>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RegisterImpressionInGA({
  logAnalyticsEvent,
  ...props
}: {
  logAnalyticsEvent?: LogAnalyticsFunctionType;
  inventorySubType: InventorySubType;
  adObj: AdDataType;
}) {
  React.useEffect(() => {
    logAnalyticsEvent?.(
      AnalyticsEventNameEnum.DIGITAL_ADS,
      {
        action: AnalyticsActionNameEnum.VIEW,
        screenType: getAnalyticsScreenType(props.inventorySubType),
        contextEntityName: props.adObj.sponsorName,
        contextEntityId: props.adObj.sponsorUrl
      },
      AnalyticsDatastoreType.MIXPANEL_BUSINESS
    );
  }, [logAnalyticsEvent, props.adObj, props.inventorySubType]);
  return null;
}
